<template>
    <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-6 tw-border tw-border-solid tw-border-primary-200">
        <!-- Enhanced Loading State -->
        <div v-if="loading" class="tw-animate-pulse">
            <!-- Header Skeleton -->
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
                <div>
                    <div class="tw-h-8 tw-bg-gray-200 tw-rounded tw-w-48 tw-mb-2"></div>
                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-64"></div>
                </div>
                <div class="tw-flex tw-items-center tw-gap-3">
                    <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-32"></div>
                    <div class="tw-h-10 tw-bg-gray-200 tw-rounded tw-w-40"></div>
                </div>
            </div>
            
            <!-- Content Skeleton -->
            <div class="tw-max-w-4xl">
                <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8">
                    <!-- Left Column - Product Information Skeleton -->
                    <div class="tw-space-y-6">
                        <div>
                            <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-40 tw-mb-4"></div>
                            
                            <!-- Title Skeleton -->
                            <div class="tw-mb-6">
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32 tw-mb-2"></div>
                                <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-full"></div>
                            </div>

                            <!-- Description Skeleton -->
                            <div class="tw-mb-6">
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-mb-2"></div>
                                <div class="tw-space-y-2">
                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-full"></div>
                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-3/4"></div>
                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-5/6"></div>
                                </div>
                            </div>

                            <!-- Categories Skeleton -->
                            <div class="tw-mb-6">
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-28 tw-mb-2"></div>
                                <div class="tw-flex tw-gap-2 tw-flex-wrap">
                                    <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-24"></div>
                                    <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-20"></div>
                                    <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-28"></div>
                                </div>
                            </div>

                            <!-- Stats Skeleton -->
                            <div class="tw-mb-6">
                                <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-mb-2"></div>
                                <div class="tw-grid tw-grid-cols-2 tw-gap-4">
                                    <div class="tw-bg-gray-50 tw-p-4 tw-rounded-lg">
                                        <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-16 tw-mb-2"></div>
                                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-12"></div>
                                    </div>
                                    <div class="tw-bg-gray-50 tw-p-4 tw-rounded-lg">
                                        <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-16 tw-mb-2"></div>
                                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-12"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Right Column - Product Image Skeleton -->
                    <div class="tw-space-y-6">
                        <div>
                            <div class="tw-h-6 tw-bg-gray-200 tw-rounded tw-w-32 tw-mb-4"></div>
                            <div class="tw-w-full tw-h-64 tw-bg-gray-200 tw-rounded-lg"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Content -->
        <div v-else>
            <!-- Header Section -->
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-8">
                <div>
                    <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-2">Product Details</h1>
                    <p class="tw-text-sm tw-text-gray-600">Viewing information for "{{ product?.name || 'Loading...' }}"</p>
                </div>
                
                <!-- Action Buttons -->
                <div class="tw-flex tw-items-center tw-gap-3">
                    <!-- Back Button -->
                    <a-button
                        @click="goBack"
                        intent="light"
                        left-icon="solar:arrow-left-linear"
                    >
                        Back to Products
                    </a-button>
                    
                    <!-- Edit Button -->
                    <a-button
                        @click="editProduct"
                        intent="primary"
                        left-icon="solar:pen-bold-duotone"
                    >
                        Edit Product
                    </a-button>
                </div>
            </div>

            <!-- Product Content -->
            <div class="tw-max-w-4xl">
                <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8">
                    <!-- Left Column - Product Information -->
                    <div class="tw-space-y-6">
                        <div>
                            <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Product Information</h2>
                            
                            <!-- Product Title -->
                            <div class="tw-mb-6">
                                <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    Product Title
                                </label>
                                <div class="tw-text-lg tw-font-medium tw-text-gray-900 tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border">
                                    {{ product?.name || 'N/A' }}
                                </div>
                            </div>

                            <!-- Product Description -->
                            <div class="tw-mb-6">
                                <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    Description
                                </label>
                                <div class="tw-text-gray-900 tw-p-3 tw-bg-gray-50 tw-rounded-md tw-border tw-min-h-[100px] tw-whitespace-pre-wrap">
                                    {{ product?.description || 'No description available' }}
                                </div>
                            </div>

                            <!-- Categories -->
                            <div class="tw-mb-6">
                                <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    Categories
                                </label>
                                <div class="tw-flex tw-gap-2 tw-flex-wrap">
                                    <span 
                                        v-for="category in product?.categories || []" 
                                        :key="category.id"
                                        class="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-font-medium tw-bg-primary-100 tw-text-primary-800"
                                    >
                                        {{ category.name }}
                                    </span>
                                    <span v-if="!product?.categories || product.categories.length === 0" class="tw-text-gray-500 tw-text-sm">
                                        No categories assigned
                                    </span>
                                </div>
                            </div>

                            <!-- Product Statistics -->
                            <div class="tw-mb-6">
                                <h3 class="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-3">Product Statistics</h3>
                                <div class="tw-grid tw-grid-cols-2 tw-gap-4">
                                    <!-- Total Views -->
                                    <div class="tw-bg-blue-50 tw-p-4 tw-rounded-lg tw-border tw-border-blue-200">
                                        <div class="tw-flex tw-items-center tw-gap-2">
                                            <icon icon="solar:eye-bold-duotone" class="tw-text-blue-600 tw-text-lg" />
                                            <span class="tw-text-sm tw-font-medium tw-text-blue-900">Total Views</span>
                                        </div>
                                        <div class="tw-text-2xl tw-font-bold tw-text-blue-700 tw-mt-1">
                                            {{ product?.total_views || 0 }}
                                        </div>
                                    </div>
                                    
                                    <!-- Total Bookmarks -->
                                    <div class="tw-bg-green-50 tw-p-4 tw-rounded-lg tw-border tw-border-green-200">
                                        <div class="tw-flex tw-items-center tw-gap-2">
                                            <icon icon="solar:bookmark-bold-duotone" class="tw-text-green-600 tw-text-lg" />
                                            <span class="tw-text-sm tw-font-medium tw-text-green-900">Total Bookmarks</span>
                                        </div>
                                        <div class="tw-text-2xl tw-font-bold tw-text-green-700 tw-mt-1">
                                            {{ product?.total_bookmarks || 0 }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Product ID -->
                            <div class="tw-mb-6">
                                <label class="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                                    Product ID
                                </label>
                                <div class="tw-text-sm tw-text-gray-600 tw-p-2 tw-bg-gray-50 tw-rounded tw-border tw-font-mono">
                                    {{ product?.id || 'N/A' }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Right Column - Product Image -->
                    <div class="tw-space-y-6">
                        <div>
                            <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">Product Image</h2>
                            
                            <!-- Image Display -->
                            <div class="tw-mb-6">
                                <div v-if="product?.thumbnail_url" class="tw-relative">
                                    <img
                                        :src="product.thumbnail_url"
                                        :alt="product.name"
                                        class="tw-w-full tw-h-64 tw-object-cover tw-rounded-lg tw-border tw-border-gray-200"
                                    />
                                </div>
                                <div v-else class="tw-w-full tw-h-64 tw-bg-gray-100 tw-rounded-lg tw-border-2 tw-border-dashed tw-border-gray-300 tw-flex tw-flex-col tw-items-center tw-justify-center">
                                    <icon icon="solar:image-bold-duotone" class="tw-text-4xl tw-text-gray-400 tw-mb-2" />
                                    <p class="tw-text-sm tw-text-gray-500">No image available</p>
                                </div>
                            </div>

                            <!-- Image Information -->
                            <div class="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
                                <h3 class="tw-text-sm tw-font-medium tw-text-blue-900 tw-mb-2">
                                    <icon icon="solar:info-circle-bold" class="tw-mr-1" />
                                    Image Information
                                </h3>
                                <ul class="tw-text-sm tw-text-blue-800 tw-space-y-1">
                                    <li>• Product images help customers understand your product better</li>
                                    <li>• High-quality images improve product visibility</li>
                                    <li>• Images are automatically optimized for web display</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import useAlert from '@/composables/useAlert'
import productApi from '@/api/products'

const router = useRouter()
const route = useRoute()

// Component state
const loading = ref(true)
const product = ref(null)

// Methods
const goBack = () => {
    router.push({ name: 'pages.company.products' })
}

const editProduct = () => {
    router.push({ 
        name: 'pages.company.products.edit', 
        params: { id: route.params.id } 
    })
}

const loadProduct = async () => {
    loading.value = true
    
    try {
        const productId = route.params.id
        const response = await productApi.get(productId)
        
        if (response.data) {
            product.value = response.data
        } else {
            throw new Error('Product not found')
        }

    } catch (error) {
        console.error('Error loading product:', error)
        useAlert('Error loading product. Please try again.', 'danger')
        goBack()
    } finally {
        loading.value = false
    }
}

// Initialize
onMounted(() => {
    loadProduct()
})
</script>

<style scoped>
/* Additional custom styles if needed */
</style> 